/**
 * Parse "\\tag{1.1}Title Text" → { tag: "1.1", name: "Title Text" }
 * @param {string} titleStr
 * @returns {{ tag: string, name: string }}
 */
export function parseTitle(titleStr) {
  const match = titleStr.match(/^\\tag\{([^}]*)\}(.*)$/s);
  if (match) return { tag: match[1].trim(), name: match[2].trim() };
  return { tag: "", name: titleStr };
}

/**
 * Process text: convert markdown and internal links to HTML, keep LaTeX for MathJax.
 * @param {string} text
 * @returns {string} HTML string
 */
export function processText(text) {
  if (!text) return "";
  let t = text;

  // Protect LaTeX blocks from newline→<br> conversion and from the double-backslash
  // normalisation below, which could mis-split commands like \nonumber into \n + onumber.
  // $$...$$ must be extracted before $...$ to avoid partial matches.
  // Display-math blocks are cleaned here: align→align* suppresses auto-numbering,
  // \tag{} removal strips manual equation numbers (same rules as ensureDisplayMath).
  const mathBlocks = [];
  const stash = (m) => {
    mathBlocks.push(m);
    return `\x00M${mathBlocks.length - 1}\x00`;
  };
  // Normalise double-escaped backslashes before LaTeX commands (e.g. \\mathbb → \mathbb).
  // Applied inside math blocks here and outside them below.
  // Safe for align-environment line breaks because \\ there is always followed by
  // whitespace or a newline, never immediately by a letter or {.
  const normBackslashes = (s) => s.replace(/\\\\(?=[a-zA-Z{])/g, "\\");

  t = t.replace(/\$\$[\s\S]*?\$\$/g, (m) =>
    stash(
      normBackslashes(
        m
          .replace(/\\begin\{align\}/g, "\\begin{align*}")
          .replace(/\\end\{align\}/g, "\\end{align*}")
          .replace(/\\tag\{[^}]*\}/g, ""),
      ),
    ),
  );
  t = t.replace(/\$[^$\n]+?\$/g, (m) => stash(normBackslashes(m)));

  // Remove tags from figure legends (runs on non-math text only, since $$...$$ is stashed)
  t = t.replace(new RegExp("\\\\tag\\{([^\\^\\s}]*)\\s*\\}", "gm"), "$1");

  // Normalise double-escaped backslashes in non-math text
  // (e.g. AI-generated content stored with \\textbf instead of \textbf).
  t = t.replace(/\\\\(?=[a-zA-Z{])/g, "\\");

  // Convert internal links to clickable support-link anchors.
  // Figure/equation refs: the URL ID does not match support.json _id values, so we
  // store the label from \ref{label}{num} in data-support-label for the click handler.
  // Regular support links store the MongoDB ID in data-support-id.
  t = t.replace(
    /\[([^\]]*)\]\(https?:\/\/internal-link(2)?(-i)?\/([a-f0-9]+)\)/gi,
    function (_, linkText, subtle, italic, id) {
      var cls = "support-link";
      if (subtle) cls += " support-link--subtle";
      if (italic) cls += " support-link--italic";
      var figPat = /^figure\s*\\ref\{([^}]*)\}\{([^}]+)\}/i;
      var figMatch = linkText.match(figPat);
      if (figMatch) {
        return (
          '<a class="' +
          cls +
          '" data-support-id="' +
          id +
          '" href="#">Figure ' +
          figMatch[2] +
          "</a>"
        );
      }
      var eqPat = /^equation\s*\\ref\{([^}]*)\}\{([^}]+)\}/i;
      var eqMatch = linkText.match(eqPat);
      if (eqMatch) {
        return (
          '<a class="' +
          cls +
          '" data-support-id="' +
          id +
          '" href="#">Equation ' +
          eqMatch[2] +
          "</a>"
        );
      }
      return (
        '<a class="' +
        cls +
        '" data-support-id="' +
        id +
        '" href="#">' +
        linkText +
        "</a>"
      );
    },
  );

  // Strip bare \ref{label}{display} → display (e.g. "Section \ref{sec:foo}{3.4}" → "Section 3.4")
  t = t.replace(/\\ref\{[^}]*\}\{([^}]+)\}/g, "$1");

  // Color spans: [https://color:name](text) → <span style="color:name">text</span>
  t = t.replace(
    /\[([^)]+)\]\(https?:\/\/color:([a-zA-Z]+|#[0-9a-fA-F]{3,8})\)/g,
    '<span style="color:$2">$1</span>',
  );

  // Figure-pointer links: [text](https://figure-pointer/0/x1/y1/1/x2/y2/...)
  // → <button class="ct__fig-ptr-btn" data-arrows="x1:y1|x2:y2">text</button>
  t = t.replace(
    /\[([^\]]+)\]\(https?:\/\/figure-pointer\/([^)]*)\)/g,
    function (_, linkText, arrowPath) {
      const parts = arrowPath.split("/");
      const pairs = [];
      for (let i = 0; i + 2 < parts.length; i += 3) {
        pairs.push(parts[i + 1] + ":" + parts[i + 2]);
      }
      return (
        '<button class="ct__fig-ptr-btn" data-arrows="' +
        pairs.join("|") +
        '">' +
        linkText +
        "</button>"
      );
    },
  );

  // Remaining links → anchor tags
  t = t.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Bold (must run before italic so ** isn't consumed by two * matches)
  t = t.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");

  // Italic
  t = t.replace(/\*([^*\n]+)\*/g, "<em>$1</em>");

  // Collapse 3+ consecutive newlines to 2
  t = t.replace(/\n{3,}/g, "\n\n");

  // Single newline → line break
  t = t.replace(/\n/g, "<br>");

  // Restore protected LaTeX blocks.
  // Wrap display-math ($$) in a span so callers can set font-size on that span
  // and have MathJax inherit it at render time (MathJax reads the parent's
  // computed font-size before it renders, so CSS on mjx-container itself is too late).
  t = t.replace(/\x00M(\d+)\x00/g, (_, i) => {
    const block = mathBlocks[parseInt(i)];
    return block.startsWith("$$")
      ? `<span class="display-math">${block}</span>`
      : block;
  });

  return t;
}

/**
 * Parse a figure-pointer arrow data string "x1:y1|x2:y2|..." into [{x,y},...].
 * @param {string} key
 * @returns {Array<{x:number, y:number}>}
 */
export function parseArrowKey(key) {
  if (!key) return [];
  return key.split("|").map((pair) => {
    const [x, y] = pair.split(":").map(Number);
    return { x, y };
  });
}

/**
 * Convert SVG string to a safe data URL for use in <img>.
 * @param {string} svg
 * @returns {string}
 */
export function svgToDataUrl(svg) {
  if (svg.startsWith("data:")) return svg;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * Flatten all frames from sections into a single ordered array.
 * @param {Array} sections
 * @returns {Array<{ frame, sectionTitle }>}
 */
export function flattenFrames(sections) {
  const out = [];
  sections?.forEach((section, sectionIndex) => {
    section.subsections.forEach((sub) => {
      sub.frames.forEach((frame) => {
        out.push({
          frame,
          sectionTitle: section.title,
          sectionId: section._id,
          sectionIndex: sectionIndex,
        });
      });
    });
  });
  return out;
}

// /**
//  * Build TOC items from sections.
//  * @param {Array} sections
//  * @returns {Array<{ frameId, tag, name, level }>}
//  */
// export function buildTOC(sections) {
//   const items = [];
//   sections?.forEach(section => {
//     section.subsections.forEach(sub => {
//       sub.frames.forEach(frame => {
//         frame.contents.forEach(content => {
//           if (content.type === 'EDCOMP_TITLE') {
//             const { tag, name } = parseTitle(content.title);
//             items.push({
//               frameId:   frame._id,
//               contentId: content._id,
//               tag,
//               name,
//               level: content.data.level,
//             });
//           }
//         });
//       });
//     });
//   });
//   return items;
// }

/**
 * Build a map: frameId → comments[]
 * @param {Array} comments
 * @returns {Object}
 */
export function buildCommentsMap(comments) {
  const map = {};
  comments?.forEach((c) => {
    const id = c.content_item_id;
    if (!map[id]) map[id] = [];
    map[id].push(c);
  });
  return map;
}

/**
 * Count total answerable problems across all sections.
 * @param {Array} sections
 * @returns {number}
 */
export function countProblems(sections) {
  let n = 0;
  sections?.forEach((section) => {
    section.subsections.forEach((sub) => {
      sub.frames.forEach((frame) => {
        frame.contents.forEach((c) => {
          if (c.type === "EDCOMP_PROBLEM_SET") n += c.data.problems.length;
        });
      });
    });
  });
  return n;
}

/* ── Selection → Markdown ──────────────────────────────────────────────────
 * Walk a cloned selection fragment and produce Markdown-flavoured text,
 * preserving bold, italic, external links, and inline/display LaTeX.
 *
 * LaTeX is recovered from MathJax's own document model
 * (window.MathJax.startup.document.math), which stores the original source
 * string alongside each rendered <mjx-container>.  We stamp that source onto
 * the live DOM nodes as temporary data-* attributes before cloning so that
 * the clones carry the source through, then remove the attributes immediately.
 * ──────────────────────────────────────────────────────────────────────── */

function _mjxSource(mjxContainer) {
  // Primary: stamp applied by selectionToMarkdown() before cloning
  if (mjxContainer.dataset.mjxLatex !== undefined) {
    const src = mjxContainer.dataset.mjxLatex;
    const isDisplay = mjxContainer.dataset.mjxDisplay === "1";
    return isDisplay ? `$$${src}$$` : `$${src}$`;
  }
  // Fallback A: <annotation encoding="application/x-tex"> in assistive MML
  const ann = mjxContainer.querySelector(
    'annotation[encoding="application/x-tex"]',
  );
  if (ann?.textContent) {
    const src = ann.textContent.trim();
    const isDisplay = mjxContainer.getAttribute("display") === "true";
    return isDisplay ? `$$${src}$$` : `$${src}$`;
  }
  // Fallback B: alttext on <math>
  const alttext = mjxContainer.querySelector("math")?.getAttribute("alttext");
  if (alttext) {
    const isDisplay = mjxContainer.getAttribute("display") === "true";
    return isDisplay ? `$$${alttext}$$` : `$${alttext}$`;
  }
  return "[math]";
}

function _childrenMd(el) {
  return [...el.childNodes].map(_nodeMd).join("");
}

function _nodeMd(node) {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const tag = node.tagName.toLowerCase();

  if (tag === "mjx-container") return _mjxSource(node);
  // Skip sub-elements of mjx-container (handled above as a unit)
  if (tag === "mjx-assistive-mml") return "";
  if (tag === "mjx-math") return "";

  if (tag === "strong" || tag === "b") return `**${_childrenMd(node)}**`;
  if (tag === "em" || tag === "i") return `*${_childrenMd(node)}*`;

  if (tag === "a") {
    const href = node.getAttribute("href");
    const text = _childrenMd(node);
    // Internal support links have no useful external URL
    if (href && href !== "#" && !node.classList.contains("support-link")) {
      return `[${text}](${href})`;
    }
    return text;
  }

  if (tag === "br") return "\n";

  // <mark> (highlight spans), <span>, <div>, unknown → just recurse
  return _childrenMd(node);
}

/**
 * Return the current browser text selection as Markdown, preserving bold,
 * italic, external links, and LaTeX (inline and display).
 * @returns {string}
 */
export function selectionToMarkdown() {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return "";

  // Stamp each live mjx-container with its original LaTeX source so the
  // attribute is copied into the cloneContents() fragment below.
  const stamped = [];
  const mjxDoc = window.MathJax?.startup?.document;
  if (mjxDoc?.math) {
    for (const item of mjxDoc.math) {
      if (item.typesetRoot && typeof item.math === "string") {
        item.typesetRoot.dataset.mjxLatex = item.math;
        item.typesetRoot.dataset.mjxDisplay = item.display ? "1" : "0";
        stamped.push(item.typesetRoot);
      }
    }
  }

  const fragment = sel.getRangeAt(0).cloneContents();

  // Remove temporary attributes from the live DOM immediately
  for (const el of stamped) {
    delete el.dataset.mjxLatex;
    delete el.dataset.mjxDisplay;
  }

  return _childrenMd(fragment).trim();
}

/**
 * Format seconds as HH:MM:SS.
 * @param {number} totalSeconds
 * @returns {string}
 */
export function formatTime(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
}

// /**
//  * Build list of all problem sets with their problems and parent frame ID.
//  * @param {Array} sections
//  * @returns {Array<{ frameId, setId, problems }>}
//  */
// export function buildProblemSetList(sections) {
//   const sets = [];
//   sections.forEach(section => {
//     section.subsections.forEach(sub => {
//       sub.frames.forEach(frame => {
//         frame.contents.forEach(c => {
//           if (c.type === 'EDCOMP_PROBLEM_SET') {
//             sets.push({
//               frameId: frame._id,
//               setId: c._id,
//               label: c.title,
//               problems: c.data.problems,
//             });
//           }
//         });
//       });
//     });
//   });
//   return sets;
// }
